import React, { useCallback, useEffect, useState } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Sketch } from '@uiw/react-color';
import { TbArrowLeft } from 'react-icons/tb';
import { useTarget } from 'providers/target/useTarget';
import { IContentsModule } from 'types';
import { UiComponent } from 'components';
import { ContentsQuery, FileServiceQuery } from 'queries';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { PLATFORM_MENU_URL } from 'data/menu';
import EditModule from '../EditModule';
import Action from './Action';
import ModuleManageList from './ModuleManageList';
import ModuleManageItem from './ModuleManageItem';
import * as S from './style';

const data: IContentsModule[] = [
  {
    id: 1,
    type: 'MAIN_IMAGE',
    data: '',
  },

  {
    id: 2,
    type: 'SUB_TITLE',
    data: '텍스트 1',
  },
  {
    id: 3,
    type: 'TITLE',
    data: '텍스트 2',
  },
  {
    id: 4,
    type: 'TEXT',
    data: '텍스트 3',
  },
  {
    id: 5,
    type: 'SUB_IMAGE',
    data: '',
  },
];

function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { target, targetList, setTargetList } = useTarget();
  const { mutateAsync: update } = ContentsQuery.useUpdate();
  const { data: content } = ContentsQuery.useGet(id || '');
  const { data: jsonData } = ContentsQuery.useGetJson(id || '');
  const { handleUploadd: upload } = FileServiceQuery.useUploadContents();
  const [backgroundColor, setBackgroundColor] = useState('#fff');
  const [showSketch, setShowSketch] = useState(false);

  const handleSave = () => {
    if (!id) return;

    const newTargetList = targetList.map((m) => {
      if (m.id === target?.id) {
        return { ...target, backgroundColor };
      }

      return { ...m, backgroundColor };
    });

    const jsonString = JSON.stringify(newTargetList, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const file = new File([blob], `${id}.json`, { type: 'application/json' });

    upload(file);
    update(
      { id: Number(id), jsonUpdatedAt: new Date() },
      {
        onSuccess: () => {
          toast.success('저장되었습니다.');
          setTargetList(newTargetList);
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleMove = (from: number, to: number) => {
    setTargetList((prev: IContentsModule[]) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleRemove = (id: number) => {
    setTargetList(targetList.filter((c) => c.id !== id));
  };

  useEffect(() => setTargetList(data), []);

  useEffect(() => {
    if (!jsonData) return;
    setBackgroundColor(jsonData[0]?.backgroundColor || '#fff');
    setTargetList(jsonData);
  }, [jsonData]);

  const handleOff = useCallback(() => setShowSketch(false), []);

  useEffect(() => {
    window.document.addEventListener('click', handleOff);

    return () => window.document.removeEventListener('click', handleOff);
  }, []);

  return (
    <div css={S.page}>
      <header css={S.topBar}>
        <button
          type="button"
          css={S.backButton}
          onClick={() => navigate(PLATFORM_MENU_URL.콘텐츠)}
          aria-label="콘텐츠 목록으로"
        >
          <TbArrowLeft size={22} />
        </button>
        <div css={S.topTitle}>
          <h1>콘텐츠 상세 구성</h1>
          {content?.title && <p>{content.title}</p>}
        </div>
        <UiComponent.Button
          variant="contained"
          color="primary"
          css={S.saveButton}
          onClick={handleSave}
        >
          저장
        </UiComponent.Button>
      </header>

      <div css={S.body}>
        <aside css={S.panel('left')}>
          <div css={S.section}>
            <p css={S.sectionTitle}>
              레이아웃
              <small>{targetList.length}개</small>
            </p>
            <DndProvider backend={HTML5Backend}>
              <ModuleManageList>
                {targetList.map((t, index) => (
                  <ModuleManageItem
                    key={String(t.id)}
                    module={t}
                    index={index}
                    handleMove={handleMove}
                    handleRemove={handleRemove}
                  />
                ))}
              </ModuleManageList>
            </DndProvider>
          </div>
        </aside>

        <section css={S.canvas}>
          <div css={S.phone(backgroundColor)}>
            {targetList.length ? (
              targetList.map((m) => (
                <EditModule key={String(m.id)} module={m} />
              ))
            ) : (
              <p css={S.emptyPhone}>블록을 추가하면 여기에 미리 보여요</p>
            )}
          </div>
        </section>

        <aside css={S.panel('right')}>
          <div css={S.section}>
            <p css={S.sectionTitle}>배경 컬러</p>
            <div css={S.colorRow}>
              <button
                type="button"
                css={S.swatchButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSketch(!showSketch);
                }}
              >
                <span css={S.swatch(backgroundColor)} />
                {backgroundColor.toUpperCase()}
              </button>
              {showSketch && (
                <div css={S.sketchPopover} onClick={(e) => e.stopPropagation()}>
                  <Sketch
                    color={backgroundColor}
                    onChange={(color) => setBackgroundColor(color.hex)}
                  />
                </div>
              )}
            </div>
          </div>
          <Action />
        </aside>
      </div>
    </div>
  );
}

export default Editor;
